/**
 * Public standalone gift-card page — /gift/:token.
 *
 * Rendered completely outside the site chrome (no Navbar/Footer/cart/widgets).
 * It never receives or renders the full Shopify code (Stage 4 OTP only), never
 * exposes ids/hashes/PII, and never treats the original amount as a balance.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Printer } from 'lucide-react';
import { LuxuryGiftCard, type LuxuryGiftCardData } from '@/components/gift/LuxuryGiftCard';
import { GiftRevealSection } from '@/components/gift/GiftRevealSection';
import { GIFT_STATUS_LABELS, giftView, type PublicGiftCard } from '@/lib/giftCards';
import { isValidPublicToken, publicGiftPageUrl, resolveBalanceView } from '@/lib/giftPublic';

const INACTIVE = ['revoked', 'expired', 'depleted', 'failed'];


interface Design {
  headline_he: string;
  terms_he: string;
  logo_url: string | null;
  bg_style: string;
  accent: string;
}

/** One generic experience for malformed tokens and unknown tokens alike. */
function NotFoundView() {
  return (
    <div dir="rtl" className="lux-page">
      <Helmet>
        <title>שובר מתנה</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <div className="max-w-sm text-center space-y-2">
        <h1 className="text-xl">הקישור אינו זמין</h1>
        <p className="text-sm opacity-70">הקישור שהגעתם דרכו אינו תקין או שאינו בתוקף.</p>
      </div>
    </div>
  );
}

export default function GiftCardView() {
  const { token = '' } = useParams();

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [card, setCard] = useState<PublicGiftCard | null>(null);
  const [design, setDesign] = useState<Design | null>(null);
  const [publicUrl, setPublicUrl] = useState<string | null>(null);

  const validToken = isValidPublicToken(token);

  useEffect(() => {
    if (!validToken) {

      setFailed(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await giftView(token);
        if (cancelled) return;
        setCard(res.card);
        setDesign((res.design as Design | null) ?? null);
        setPublicUrl(res.public_url ?? null);
      } catch {
        // Never log or report the token — the generic view covers every failure.
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, validToken]);

  const data: LuxuryGiftCardData | null = useMemo(() => {
    // No public preview bypass: demo data exists only in the admin preview page.
    if (!card) return null;

    const balance = resolveBalanceView(card);
    return {
      headline: design?.headline_he ?? 'שובר מתנה',
      recipientName: card.recipient_name,
      senderName: card.sender_name,
      message: card.message,
      occasion: card.occasion,
      initialAmount: card.initial_amount,
      balanceAmount: balance.amount,
      balanceAvailable: balance.available,
      balanceSyncedAt: balance.syncedAt,
      currency: card.currency || 'ILS',
      statusLabel: GIFT_STATUS_LABELS[card.status] ?? card.status,
      active: !INACTIVE.includes(card.status),
      expiresOn: card.expires_on,
      lastCharacters: card.last_characters,
      logoUrl: design?.logo_url ?? null,
      qrValue: publicUrl ?? publicGiftPageUrl(window.location.origin, token),
    };
  }, [card, design, publicUrl, token]);

  if (loading) {
    return (
      <div dir="rtl" className="lux-page">
        <Loader2 className="h-5 w-5 animate-spin" aria-label="טוען" />
      </div>
    );
  }

  if (failed || !data) return <NotFoundView />;

  return (
    <div dir="rtl" lang="he" className="lux-page">
      <Helmet>
        <title>{`${data.headline} · ${data.recipientName}`}</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
        <meta name="description" content="שובר מתנה דיגיטלי" />
      </Helmet>

      <h1 className="sr-only">{`${data.headline} עבור ${data.recipientName}`}</h1>

      <LuxuryGiftCard data={data} />

      {data.active && <GiftRevealSection token={token} />}

      <div className="lux-no-print flex flex-col items-center gap-3 text-center">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm text-[#f6e7dd] hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd7c9]"
        >
          <Printer className="h-4 w-4" aria-hidden="true" /> הדפסה / שמירה כ‑PDF
        </button>
        {design?.terms_he && <p className="max-w-md text-[11px] leading-relaxed opacity-60">{design.terms_he}</p>}
      </div>

    </div>
  );
}
